import { RimbuError } from '@rimbu/base';
import { NonEmptyBase, } from '@rimbu/collection-types/map-custom';
import { TraverseState, } from '@rimbu/common';
import { isEmptyStreamSourceInstance } from '@rimbu/stream/custom';
export class OrderedMapNonEmpty extends NonEmptyBase {
    constructor(context, keyOrder, sourceMap) {
        super();
        this.context = context;
        this.keyOrder = keyOrder;
        this.sourceMap = sourceMap;
    }
    copy(keyOrder = this.keyOrder, sourceMap = this.sourceMap) {
        if (keyOrder === this.keyOrder && sourceMap === this.sourceMap) {
            return this;
        }
        return this.context.createNonEmpty(keyOrder, sourceMap);
    }
    get size() {
        return this.keyOrder.length;
    }
    stream() {
        return this.streamKeys().map((k) => [
            k,
            this.sourceMap.get(k, RimbuError.throwInvalidStateError),
        ]);
    }
    streamKeys() {
        return this.keyOrder.stream();
    }
    streamValues() {
        return this.streamKeys().map((k) => this.sourceMap.get(k, RimbuError.throwInvalidStateError));
    }
    hasKey(key) {
        return this.sourceMap.hasKey(key);
    }
    get(key, otherwise) {
        return this.sourceMap.get(key, otherwise);
    }
    set(key, value) {
        let newKeyOrder = this.keyOrder;
        const newSourceMap = this.sourceMap.modifyAt(key, {
            ifNew: () => {
                newKeyOrder = newKeyOrder.append(key);
                return value;
            },
            ifExists: () => value,
        });
        return this.copy(newKeyOrder, newSourceMap);
    }
    addEntry(entry) {
        return this.set(entry[0], entry[1]);
    }
    addEntries(entries) {
        if (isEmptyStreamSourceInstance(entries))
            return this;
        const builder = this.toBuilder();
        builder.addEntries(entries);
        return builder.build().assumeNonEmpty();
    }
    removeKey(key) {
        if (!this.context.mapContext.isValidKey(key))
            return this;
        const newSourceMap = this.sourceMap.removeKey(key);
        if (newSourceMap === this.sourceMap)
            return this;
        if (newSourceMap.nonEmpty()) {
            const index = this.keyOrder.stream().indexOf(key);
            const newKeyOrder = this.keyOrder.remove(index);
            if (newKeyOrder.nonEmpty()) {
                return this.copy(newKeyOrder, newSourceMap);
            }
        }
        return this.context.empty();
    }
    removeKeys(keys) {
        if (isEmptyStreamSourceInstance(keys))
            return this;
        const builder = this.toBuilder();
        builder.removeKeys(keys);
        return builder.build();
    }
    removeKeyAndGet(key) {
        if (!this.context.mapContext.isValidKey(key)) {
            return undefined;
        }
        const removeSourceResult = this.sourceMap.removeKeyAndGet(key);
        if (undefined === removeSourceResult) {
            return undefined;
        }
        const [newSourceMap, removedValue] = removeSourceResult;
        if (newSourceMap.nonEmpty()) {
            const index = this.keyOrder.stream().indexOf(key);
            const newKeyOrder = this.keyOrder.remove(index);
            if (newKeyOrder.nonEmpty()) {
                return [this.copy(newKeyOrder, newSourceMap), removedValue];
            }
        }
        return [this.context.empty(), removedValue];
    }
    modifyAt(key, options) {
        let newKeyOrder = this.keyOrder;
        const result = this.sourceMap.modifyAt(key, options);
        if (result === this.sourceMap)
            return this;
        if (result.isEmpty)
            return this.context.empty();
        if (result.size < this.sourceMap.size) {
            const index = this.keyOrder.stream().indexOf(key);
            newKeyOrder = newKeyOrder.remove(index);
        }
        else if (result.size > this.sourceMap.size) {
            newKeyOrder = newKeyOrder.append(key);
        }
        if (result.nonEmpty() && newKeyOrder.nonEmpty()) {
            return this.copy(newKeyOrder, result);
        }
        return this.context.empty();
    }
    forEach(f, options = {}) {
        const { reversed = false, state = TraverseState() } = options;
        if (state.halted)
            return;
        const keyIter = this.keyOrder.stream({ reversed })[Symbol.iterator]();
        const done = Symbol('Done');
        let key;
        const sourceMap = this.sourceMap;
        const { halt } = state;
        while (!state.halted && done !== (key = keyIter.fastNext(done))) {
            f([key, sourceMap.get(key, RimbuError.throwInvalidStateError)], state.nextIndex(), halt);
        }
    }
    filter(pred, options = {}) {
        const { negate = false } = options;
        const builder = this.context.builder();
        builder.addEntries(this.stream().filter(pred, { negate }));
        if (builder.size === this.size)
            return this;
        return builder.build();
    }
    mapValues(mapFun) {
        return this.context.createNonEmpty(this.keyOrder, this.sourceMap.mapValues(mapFun));
    }
    updateAt(key, update) {
        return this.copy(this.keyOrder, this.sourceMap.updateAt(key, update));
    }
    toArray() {
        return this.stream().toArray();
    }
    toBuilder() {
        return this.context.createBuilder(this);
    }
    toString() {
        return this.stream().join({
            start: 'OrderedMap(',
            sep: ', ',
            end: ')',
            valueToString: (entry) => `${entry[0]} -> ${entry[1]}`,
        });
    }
    toJSON() {
        return {
            dataType: this.context.typeTag,
            value: this.sourceMap.toJSON().value,
        };
    }
}
//# sourceMappingURL=non-empty.mjs.map